/**
 * ActivityStep component - Step 4 of the 4-step check-in flow.
 * Captures previous day's activity type, intensity, duration, and steps.
 * Intensity and duration are hidden when activity_type is 'rest_day'.
 */

import React from 'react';
import { ActivityType, ActivityIntensity } from '../../types';

export interface ActivityStepData {
  activity_type?: ActivityType;
  activity_intensity?: ActivityIntensity;
  activity_duration?: number;
  steps?: number;
}

interface ActivityStepProps {
  data: ActivityStepData;
  onChange: (data: ActivityStepData) => void;
}

const ACTIVITY_TYPE_OPTIONS: { value: ActivityType; label: string }[] = [
  { value: 'rest_day', label: 'Rest day' },
  { value: 'walking', label: 'Walking' },
  { value: 'strength', label: 'Strength' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'mixed', label: 'Mixed' },
];

const ACTIVITY_INTENSITY_OPTIONS: { value: ActivityIntensity; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'intense', label: 'Intense' },
];

const ActivityStep: React.FC<ActivityStepProps> = ({ data, onChange }) => {
  const update = (partial: Partial<ActivityStepData>) => {
    onChange({ ...data, ...partial });
  };

  const handleActivityTypeChange = (type: ActivityType) => {
    if (type === 'rest_day') {
      update({ activity_type: type, activity_intensity: undefined, activity_duration: undefined });
    } else {
      update({ activity_type: type });
    }
  };

  const isRestDay = data.activity_type === 'rest_day';

  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
        Yesterday's Activity
      </h2>
      <p className="text-neutral-600 mb-6">
        What did you do yesterday? This helps us understand your recovery needs.
      </p>

      {/* Activity Type */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Activity type <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ACTIVITY_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleActivityTypeChange(option.value)}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                data.activity_type === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Intensity - hidden when rest_day */}
      {!isRestDay && data.activity_type && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Intensity
          </label>
          <div className="grid grid-cols-3 gap-2">
            {ACTIVITY_INTENSITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => update({ activity_intensity: option.value })}
                className={`p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                  data.activity_intensity === option.value
                    ? 'border-primary-600 bg-primary-50 shadow-sm'
                    : 'border-primary-200 bg-white hover:border-primary-400'
                }`}
              >
                <span className="text-sm font-medium text-neutral-700">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Duration - hidden when rest_day */}
      {!isRestDay && data.activity_type && (
        <div className="mb-6">
          <label htmlFor="activity-duration" className="block text-sm font-medium text-neutral-700 mb-2">
            Duration
          </label>
          <div className="flex items-center gap-2">
            <input
              id="activity-duration"
              type="number"
              value={data.activity_duration ?? ''}
              onChange={(e) => update({
                activity_duration: e.target.value ? parseInt(e.target.value) : undefined
              })}
              className="input w-32"
              placeholder="e.g. 45"
              min="1"
              max="480"
            />
            <span className="text-sm text-neutral-500">minutes</span>
          </div>
        </div>
      )}

      {/* Steps */}
      <div>
        <label htmlFor="steps" className="block text-sm font-medium text-neutral-700 mb-2">
          Steps yesterday <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <input
          id="steps"
          type="number"
          value={data.steps ?? ''}
          onChange={(e) => update({
            steps: e.target.value ? parseInt(e.target.value) : undefined
          })}
          className="input w-40"
          placeholder="e.g. 8000"
          min="0"
          max="100000"
        />
      </div>
    </div>
  );
};

export default ActivityStep;
